import { createEntityAdapter, createSlice } from "@reduxjs/toolkit";
import { useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import APICall from "./apiCall.js";
import { getPanelToken } from "./auth/AuthActions.js";


export const generatePanelId = (hwaddr, networkId) => [hwaddr, networkId].join("_");

const panelEntityAdapter = createEntityAdapter({
    selectId: ({ hwaddr, networkId }) => generatePanelId(hwaddr, networkId),
});

const entitySelectors = panelEntityAdapter.getSelectors(
    (state) => state.panels
);

export const panelsSlice = createSlice({
    name: "panels",
    initialState: panelEntityAdapter.getInitialState(),
    reducers: {
        panelsSet: (state, action) => {
            panelEntityAdapter.setMany(state, action.payload);
        },

        clear: panelEntityAdapter.removeAll,

        panelUpdateStrings: {
            prepare: (hwaddr, networkId, strName, strSite, strCompany, strPhoneNumber) => {
                return { payload: { hwaddr, networkId, strings: { strName, strSite, strCompany, strPhoneNumber } } }
            },
            reducer: (state, action ) => {
                panelEntityAdapter.updateOne(state, {
                    id: generatePanelId(action.payload.hwaddr, action.payload.networkId),
                    changes: action.payload.strings
                })
            }
        },

        panelUpdateZone: {
            reducer: (state, action) => {
                const panel = state.entities[generatePanelId(action.payload.hwaddr, action.payload.networkId)];
                
                if(panel){
                    panel.zones.splice(action.payload.zone.id, 1, Object.assign({}, action.payload.zone));
                }
            },
            prepare: (hwaddr, networkId, zoneId, name, mode) => {
                return { payload: { hwaddr, networkId, zone: { id: zoneId, name, mode } } }
            },
        },

        panelUpdateDeviceName: {
            reducer: (state, action) => {
                const panel = state.entities[generatePanelId(action.payload.hwaddr, action.payload.networkId)];
                
                if(panel){
                    const module = panel.modules.find( ({ port_id }) => port_id == action.payload.port_id);
                    if(module)
                    {
                        const device = module.devices.find( ({ mod_idx }) => mod_idx == action.payload.mod_idx);
                        if(device)
                        {
                            device.name = action.payload.name
                        }
                    }
                    // panel.zones.splice(action.payload.zone.id, 1, Object.assign({}, action.payload.zone));
                }
            },
            prepare: (hwaddr, networkId, port_id, mod_idx, name) => {
                return { payload: { hwaddr, networkId, port_id, mod_idx, name } };
            }
        },

        panelUpdateDeviceZone: {
            reducer: (state, action) => {
                const panel = state.entities[generatePanelId(action.payload.hwaddr, action.payload.networkId)];
                
                if(panel){
                    const module = panel.modules.find( ({ port_id }) => port_id == action.payload.port_id);
                    if(module)
                    {
                        const device = module.devices.find( ({ mod_idx }) => mod_idx == action.payload.mod_idx);
                        if(device)
                        {
                            device.zone = action.payload.zone
                        }
                    }
                    // panel.zones.splice(action.payload.zone.id, 1, Object.assign({}, action.payload.zone));
                }
            },
            prepare: (hwaddr, networkId, port_id, mod_idx, zone) => {
                return { payload: { hwaddr, networkId, port_id, mod_idx, zone } };
            }
        }
    },
});

// Actions

export function panelsFetch(hwaddr) {
    return async (dispatch) => {
        await dispatch(getPanelToken());
        const panels = await APICall(`/v2/sites/${hwaddr}`, null, null, "claims");
        dispatch(panelsSlice.actions.panelsSet(panels));
    };
}

export const panelUpdateStrings = panelsSlice.actions.panelUpdateStrings;

// Selectors



export const selectPanel = (hwaddr, networkId) => state => entitySelectors.selectById(state, generatePanelId(hwaddr, networkId));

export const selectPanelsForSite = (hwaddr) => state => entitySelectors.selectAll(state).filter( p => p.hwaddr == hwaddr );

export const selectPanelZones = (hwaddr, networkId) => state => {
    const panel = selectPanel(hwaddr, networkId)(state);
    if(panel){
        return panel.zones
    }
    return [];
}

export const selectPanelZone = (hwaddr, networkId, zoneId) => state => {
    const panel = selectPanel(hwaddr, networkId)(state);
    if(panel){
        return panel.zones.find( z => z.id == zoneId);
    }
    return null;
}

export const selectPanelZoneName = (hwaddr, networkId, zoneId) => state => {
    const zone = selectPanelZone(hwaddr, networkId, zoneId)(state);
    if(zone){
        return zone.name;
    }
    return `zone (${zoneId})`;
}

export const selectPanelModule = (hwaddr, networkId, portId) => state => {
    const panel = selectPanel(hwaddr, networkId)(state);
    if(panel){
        return panel.modules.find(m => m.port_id == portId);
    }

    return null;
}

export const selectPanelModuleDevice = (hwaddr, networkId, portId, deviceId) => state => {
    const panelModule = selectPanelModule(hwaddr, networkId, portId)(state);
    if(panelModule){
        return panelModule.devices.find( d => d.mod_idx == deviceId);
    }
    return null;
}

export const selectZonesInUseCount = (hwaddr, networkID) => state => {
    const panel = selectPanel(hwaddr, networkID)(state);
    if(panel){
        const zoneIds = new Set(panel.modules.flatMap( m => m.devices.map( d => d.zone )));
        return zoneIds.size;
    }
    return 0;
}

export const selectModuleDevicesGroupedByZone = (hwaddr, networkId, showEmpty, filter) => state =>  {

    const panel = selectPanel(hwaddr, networkId)(state);

    if(panel){
        const filteredZones = panel.zones.filter( z => z.name.toLowerCase().indexOf(filter.toLowerCase()) !== -1).map(z => z.id);
        const nameFilter = l => l.name.toLowerCase().indexOf(filter.toLowerCase()) !== -1 || filteredZones.indexOf(l.zone) !== -1;
        
        const devices = panel.modules.flatMap( m =>  m.devices.filter(nameFilter).map( d => Object.assign({}, d, { port_id: m.port_id })) );

        return panel.zones
            .map( z => Object.assign({}, z, { devices: devices.filter( d => d.zone == z.id ) }))
            .filter( z => z.devices.length > 0 || showEmpty)
        ;
        
    }
    return [];


}

// hooks
export const usePanels = (hwaddr) => useSelector(selectPanelsForSite(hwaddr));

export const usePanel = (hwaddr, networkId) => useSelector(selectPanel(hwaddr, networkId));

export const useZones = (hwaddr, networkId) => useSelector(selectPanelZones(hwaddr, networkId));

export const useZonesInUseCount = (hwaddr, networkId) => useSelector(selectZonesInUseCount(hwaddr, networkId));

export const useModules = (hwaddr, networkId) =>
    useSelector((state) => {
        const panel = selectPanel(hwaddr, networkId)(state);
        if (panel) {
            return panel.modules;
        } else {
            return [];
        }
    });


export const useHwaddrAndNetworkIdFromRouter = (hook) => {
    const { hwaddr, networkId } = useParams();
    return hook(hwaddr, networkId);
};



export const usePanelModuleFromRouter = () => 
{
    const { hwaddr, networkId, portId } = useParams();
    return useSelector(selectPanelModule(hwaddr, networkId, portId))
}

export const usePanelModuleDeviceFromRouter = () => 
{
    const { hwaddr, networkId, portId, deviceId } = useParams();
    return useSelector(selectPanelModuleDevice( hwaddr, networkId, portId, deviceId ));
}

export const useModuleDevicesGroupedByZoneFromRouter = (showEmpty, filter) => {
    const { hwaddr, networkId } = useParams();
    const selector = useCallback(selectModuleDevicesGroupedByZone(hwaddr, networkId, showEmpty, filter), [hwaddr, networkId, showEmpty, filter]);

    return useSelector(selector);
     
    
}

export const usePanelUpdateAPI = () => {
    const { hwaddr, networkId } = useParams();

    const initialDispatch = useDispatch();

    return useMemo(
        () => ({
            updateZone(id, name, mode) {
                return initialDispatch(async (dispatch) => {
                    await dispatch(getPanelToken());
                    await APICall(
                        `/v2/sites/${hwaddr}/panel/${networkId}/zone`,
                        { id, name, mode },
                        {},
                        "claims"
                    );
                    await dispatch(panelsSlice.actions.panelUpdateZone(hwaddr, networkId, id, name, mode));
                });
            },

            updateDeviceName(port_id, mod_idx, name) {
                return initialDispatch(async (dispatch) => {
                    await dispatch(getPanelToken());
                    await APICall(
                        `/v2/sites/${hwaddr}/panel/${networkId}/tmp-name`,
                        { port_id, mod_idx, name },
                        {},
                        "claims"
                    );
                    // TODO - Update device
                    await dispatch(panelsSlice.actions.panelUpdateDeviceName(hwaddr, networkId, port_id, mod_idx, name));
                });
            },

            updateDeviceZone(port_id, mod_idx, zoneId) {
                return initialDispatch(async (dispatch) => {
                    await dispatch(getPanelToken());
                    await APICall(
                        `/v2/sites/${hwaddr}/panel/${networkId}/tmp-zone`,
                        { port_id, mod_idx, zoneId },
                        {},
                        "claims"
                    );
                    // TODO - Update device
                    await dispatch(panelsSlice.actions.panelUpdateDeviceZone(hwaddr, networkId, port_id, mod_idx, zoneId));
                });
            },

        }),
        [hwaddr, networkId]
    );
};

export default panelsSlice.reducer;
